import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useMachineOEEList,
  useOEEOverview,
  useActiveStops,
  getOEEColor,
  getOEEStatus,
  getStopTypeLabel,
  getStopTypeColor,
} from '@/hooks/useMachineOEE';
import {
  Gauge,
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
  TrendingUp,
  Settings,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

interface OEEGaugeProps {
  value: number;
  label: string;
  size?: 'sm' | 'md' | 'lg';
}

function OEEGauge({ value, label, size = 'md' }: OEEGaugeProps) {
  const sizes = {
    sm: { wrapper: 'w-16 h-16', text: 'text-sm', label: 'text-xs' },
    md: { wrapper: 'w-24 h-24', text: 'text-xl', label: 'text-sm' },
    lg: { wrapper: 'w-32 h-32', text: 'text-3xl', label: 'text-sm' },
  };

  const getColor = (v: number) => {
    if (v >= 85) return '#22c55e';
    if (v >= 65) return '#eab308';
    return '#ef4444';
  };

  const radius = size === 'lg' ? 56 : size === 'md' ? 40 : 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = `${(value / 100) * circumference} ${circumference}`;

  return (
    <div className="text-center">
      <div className={cn('relative mx-auto', sizes[size].wrapper)}>
        <svg className={cn('transform -rotate-90', sizes[size].wrapper)}>
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke="currentColor"
            strokeWidth={size === 'lg' ? 12 : size === 'md' ? 8 : 6}
            fill="none"
            className="text-muted/20"
          />
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke={getColor(value)}
            strokeWidth={size === 'lg' ? 12 : size === 'md' ? 8 : 6}
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn('font-bold', sizes[size].text)}>{value.toFixed(1)}%</span>
        </div>
      </div>
      <p className={cn('text-muted-foreground mt-2', sizes[size].label)}>{label}</p>
    </div>
  );
}

export function OEEDashboard() {
  const { data: machines, isLoading: loadingMachines } = useMachineOEEList();
  const { data: overview, isLoading: loadingOverview } = useOEEOverview();
  const { data: activeStops, isLoading: loadingStops } = useActiveStops();

  const isLoading = loadingMachines || loadingOverview || loadingStops;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Cargando datos de OEE...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">OEE Promedio</p>
                <p className={cn('text-3xl font-bold', getOEEColor(overview?.avgOEE || 0))}>
                  {(overview?.avgOEE || 0).toFixed(1)}%
                </p>
                <Badge variant="outline" className="mt-2">
                  {getOEEStatus(overview?.avgOEE || 0)}
                </Badge>
              </div>
              <div className="p-3 rounded-xl bg-primary/10">
                <Gauge className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Disponibilidad</p>
                <p className="text-3xl font-bold text-green-600">
                  {(overview?.avgAvailability || 0).toFixed(1)}%
                </p>
                <Progress value={overview?.avgAvailability || 0} className="h-2 mt-2" />
              </div>
              <div className="p-3 rounded-xl bg-green-500/10">
                <Clock className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rendimiento</p>
                <p className="text-3xl font-bold text-blue-600">
                  {(overview?.avgPerformance || 0).toFixed(1)}%
                </p>
                <Progress value={overview?.avgPerformance || 0} className="h-2 mt-2" />
              </div>
              <div className="p-3 rounded-xl bg-blue-500/10">
                <TrendingUp className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Calidad</p>
                <p className="text-3xl font-bold text-purple-600">
                  {(overview?.avgQuality || 0).toFixed(1)}%
                </p>
                <Progress value={overview?.avgQuality || 0} className="h-2 mt-2" />
              </div>
              <div className="p-3 rounded-xl bg-purple-500/10">
                <CheckCircle className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* OEE por Máquina */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              OEE por Máquina
            </CardTitle>
            <CardDescription>Eficiencia de los últimos 30 días</CardDescription>
          </CardHeader>
          <CardContent>
            {machines && machines.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Máquina</TableHead>
                    <TableHead className="text-center">Disp.</TableHead>
                    <TableHead className="text-center">Rend.</TableHead>
                    <TableHead className="text-center">Cal.</TableHead>
                    <TableHead className="text-center">OEE</TableHead>
                    <TableHead className="text-center">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {machines.map((machine) => (
                    <TableRow key={machine.machine_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{machine.machine_name}</p>
                          <p className="text-xs text-muted-foreground">{machine.machine_code}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-green-600">{machine.availability.toFixed(1)}%</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-blue-600">{machine.performance.toFixed(1)}%</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-purple-600">{machine.quality.toFixed(1)}%</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={cn('font-bold', getOEEColor(machine.oee))}>
                          {machine.oee.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {machine.active_stops > 0 ? (
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {machine.active_stops} parada{machine.active_stops > 1 ? 's' : ''}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            <Activity className="w-3 h-3 mr-1" />
                            Operativa
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Settings className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay datos de producción registrados</p>
                <p className="text-sm">Registra producción para calcular el OEE</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Paradas Activas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Paradas Activas
            </CardTitle>
            <CardDescription>
              {activeStops?.length || 0} parada{(activeStops?.length || 0) !== 1 ? 's' : ''} sin resolver
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activeStops && activeStops.length > 0 ? (
              <div className="space-y-3">
                {activeStops.map((stop) => (
                  <div
                    key={stop.id}
                    className="p-3 rounded-lg border bg-muted/30"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-sm">{stop.machine_name}</p>
                        <Badge className={cn('mt-1', getStopTypeColor(stop.stop_type))}>
                          {getStopTypeLabel(stop.stop_type)}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(stop.started_at), {
                            addSuffix: true,
                            locale: es,
                          })}
                        </p>
                        {stop.current_duration_minutes && (
                          <p className="text-sm font-medium text-red-600">
                            {Math.round(stop.current_duration_minutes)} min
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">{stop.reason}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>Sin paradas activas</p>
                <p className="text-sm">Todas las máquinas operativas</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* OEE Gauges */}
      {machines && machines.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resumen Visual OEE</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {machines.slice(0, 6).map((machine) => (
                <div key={machine.machine_id} className="text-center">
                  <OEEGauge value={machine.oee} label={machine.machine_name} size="md" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default OEEDashboard;
